import { environment } from '@environment';

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { lastValueFrom } from 'rxjs';

import { GtmService } from '@app/core/services/analytics/gtm.service';
import { OrderApiService } from '@app/core/http/order-api.service';
import { UtilityService } from '@app/core/services/util/utility.service';
import { ToastrService } from '@app/core/services/notification/toastr.service';

import { MailInfo, Refund } from '@app/shared/models/api-response/customer-refunds-response';
import { OrderDetailsApiResponse } from '@app/shared/models/api-response/order-details-response';
import { DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-refund-info',
    templateUrl: './refund-info.component.html',
    styleUrls: ['./refund-info.component.scss'],
    standalone: true,
    imports: [DatePipe, MatIcon]
})
export class RefundInfoComponent implements OnInit {
    @Input() refund: Refund;
    @Input() mailInfo: MailInfo;
    @Input() isOrderSummaryPage = false;

    @Output() openOrderDetailsModal = new EventEmitter<OrderDetailsApiResponse['order']>();

    DEPLOY_URL: string;
    fetchOrderInProgress = false;
    diffDays: number;

    constructor(
        private gtmService: GtmService,
        private orderApiService: OrderApiService,
        private toastrService: ToastrService,
        private utilityService: UtilityService
    ) {
        this.DEPLOY_URL = environment.DEPLOY_URL;
    }

    ngOnInit(): void {
        this.diffDays = this.utilityService.dateDifference(new Date(this.refund.last_updated * 1000));
    }

    copyCode(code: string) {
        this.utilityService.copyCode(code, 'RRN copied');
        this.gtmService.logWebAnalyticsEvent('refund', null, 'User Clicks On Copy RRN');

        this.gtmService.logEvent("web_analytics", {
            event_action: "refund",
            event_label: "copy_rrn",
            value1: `days_lapsed: ${this.diffDays}`
        });
    }

    showOrderDetails() {
        const { tracking_id } = this.refund;
        this.fetchOrderInProgress = true;

        lastValueFrom(this.orderApiService.getLiveOrders(tracking_id))
            .then((res: OrderDetailsApiResponse) => {
                if (res.meta.code === 200) {
                    this.openOrderDetailsModal.emit(res.order);
                } else {
                    this.toastrService.openSnackBar(res.meta.message, 'OKAY');
                }
            })
            .catch(err => {
                this.toastrService.openSnackBar(err?.meta?.message, 'OKAY');
            })
            .finally(() => {
                this.fetchOrderInProgress = false;
            });
    }


    sendMailToUs() {

        this.gtmService.logEvent("web_analytics", {
            event_action: "refund",
            event_label: "initiate_help_mail",
            value1: `days_lapsed: ${this.diffDays}`
        });

        const { to, subject, body } = this.mailInfo;
        const { tracking_id, payment_txn_id } = this.refund;
        
        let updatedSubject = subject;
        let updatedBody = body;
    
        const missingPlaceholders = [];
        if (!subject.includes("{{tracking_id}}") || !body.includes("{{tracking_id}}")) {
            missingPlaceholders.push('Tracking ID');
        }

        if (!body.includes("{{lapsed_days}}")) {
            missingPlaceholders.push('Lapsed days');
        }
    
        if (missingPlaceholders.length) {
            this.gtmService.logWebAnalyticsEvent(
                'refund',
                `Missing placeholders in mail: ${missingPlaceholders.join(', ')} - body: ${body}, subject: ${subject}`
            );
        }
    
        const uniqueInfo = tracking_id || payment_txn_id;
        updatedSubject = updatedSubject.replace('{{tracking_id}}', uniqueInfo);
        updatedBody = updatedBody.replace('{{tracking_id}}', uniqueInfo).replace('{{lapsed_days}}', String(this.diffDays));
    
        const mailtoLink = `mailto:${to}?subject=${encodeURIComponent(updatedSubject)}&body=${encodeURIComponent(updatedBody)}`;
        window.location.href = mailtoLink;
    }
}